/* Hero ground: light blue band, ink type, red/blue/green figures. The rule at the bottom closes it. */
.hero {
  position: relative;
  background: var(--color-primary-blue-100); /* PRIMARY_BLUE_100 */
  color: var(--color-primary-grey-900); /* PRIMARY_GREY_900 */
  padding: 30px 0 0;
}

.hero__breadcrumb {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 11px; /* COPY_XS */
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-primary-sand-900); /* PRIMARY_SAND_900 */
}

.hero__breadcrumb a {
  color: inherit;
}

.hero__breadcrumb a:hover {
  color: var(--color-primary-grey-900); /* PRIMARY_GREY_900 */
}

.hero__breadcrumbSep {
  opacity: 0.55;
}

/* DEVIATION: the design sets this tag in ink; it uses the other About heroes' blue instead, so the
   breadcrumb reads the same across the section. */
.hero__breadcrumbTag {
  background: var(--color-primary-blue-900); /* PRIMARY_BLUE_900 */
  color: var(--color-primary-grey-000);
  padding: 5px 13px;
  border-radius: 9999px;
  letter-spacing: 0.06em;
}

.hero__title {
  font-family: var(--plume-font-display, sofia-pro, sans-serif);
  font-weight: 900;
  /* HACK: fluid display size — candidate future DS heading scale; larger than HEADING_3XL (76px). */
  font-size: clamp(40px, 6vw, 86px);
  line-height: 0.95;
  letter-spacing: -0.035em;
  margin: clamp(34px, 4vw, 48px) 0 0;
  max-width: 15ch;
}

/* Highlighter swipe: a band painted across the lower 56% of its own line rather than a
   `text-decoration`, so it sits behind the text and cannot bleed into the line above when the title
   wraps. `box-decoration-break: clone` repaints it per line. */
.hero__titleHighlight {
  color: var(--color-primary-grey-900); /* PRIMARY_GREY_900 */
  background: linear-gradient(var(--color-complementary-yellow), var(--color-complementary-yellow))
    /* COMPLEMENTARY_YELLOW */ 0 100% / 100% 56% no-repeat;
  padding: 0 0.08em;
  -webkit-box-decoration-break: clone;
  box-decoration-break: clone;
}

/* Sizes differ per figure so 6, 9 and 11 digits fill comparable optical widths. */
.figs {
  display: grid;
  grid-template-columns: 0.74fr 1.3fr 0.96fr;
  grid-template-rows: auto auto;
  row-gap: 24px;
  margin-top: clamp(44px, 5.4vw, 76px);
  border-top: 2px solid rgba(35, 34, 33, 0.22); /* HACK: a tint of PRIMARY_GREY_900, no DS token */
}

.fig {
  position: relative;
  padding: 32px 26px 38px;
  border-right: 2px solid rgba(35, 34, 33, 0.22); /* HACK: as above */
  display: grid;
  /* Subgrid keeps the three labels on one baseline despite the differing figure sizes. */
  grid-template-rows: subgrid;
  grid-row: span 2;
}

.fig:first-child {
  padding-left: 0;
}

.fig:last-child {
  border-right: none;
  padding-right: 0;
}

.fig__num {
  align-self: end;
  font-family: var(--plume-font-display, sofia-pro, sans-serif);
  font-weight: 900;
  line-height: 0.82;
  letter-spacing: -0.042em;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/* HACK: three fluid display sizes, tuned per digit count — candidate future DS heading scale. */
.figOne {
  font-size: clamp(44px, 5.9vw, 84px);
  color: var(--color-primary-red-500); /* PRIMARY_RED_500 */
}

.figTwo {
  font-size: clamp(34px, 5.1vw, 73px);
  color: var(--color-primary-blue-900); /* PRIMARY_BLUE_900 */
}

.figThree {
  font-size: clamp(36px, 5.4vw, 77px);
  color: var(--color-secondary-green-800); /* SECONDARY_GREEN_800 */
}

.fig__lbl {
  font-size: 13px; /* HEADING_5XS */
  font-weight: 800;
  letter-spacing: 0.11em;
  text-transform: uppercase;
  color: var(--color-primary-sand-900); /* PRIMARY_SAND_900 */
}

.hero__foot {
  display: flex;
  align-items: baseline;
  gap: 14px 26px;
  flex-wrap: wrap;
  padding: 30px 0 0;
}

.hero__foot p {
  font-size: 14px; /* COPY_S */
  line-height: 1.55;
  color: var(--color-primary-sand-900); /* PRIMARY_SAND_900 */
  max-width: 96ch;
  margin: 0;
}

.hero__thanks {
  margin: clamp(40px, 4.6vw, 64px) 0 clamp(36px, 4vw, 52px);
  text-align: center;
  font-size: 18px; /* COPY_L */
  font-weight: 500;
  color: var(--color-primary-sand-900); /* PRIMARY_SAND_900 */
  line-height: 1.65;
}

/* `_shared/base.css` resets only `body`, so these would inherit the browser's 1em block margins and
   open a gap the design does not have. The two lines are spaced by line-height alone. */
.hero__thanks p {
  margin: 0;
}

.hero__heart {
  display: inline-flex;
  vertical-align: -2px;
  color: var(--color-primary-red-500); /* PRIMARY_RED_500 */
}

.hero__thanks a {
  color: var(--color-primary-grey-900); /* PRIMARY_GREY_900 */
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 3px;
  text-decoration-thickness: 2px;
  text-decoration-color: var(--color-primary-red-500); /* PRIMARY_RED_500 */
  transition: color 0.18s cubic-bezier(0.04, 0.4, 0.5, 0.95);
}

.hero__thanks a:hover {
  color: var(--color-primary-red-500); /* PRIMARY_RED_500 */
}

.hero__rule {
  height: 6px;
  background: var(--color-complementary-yellow); /* COMPLEMENTARY_YELLOW */
}

@media (max-width: 700px) {
  .figs {
    grid-template-columns: 1fr;
  }

  .fig {
    border-right: none;
    border-bottom: 2px solid rgba(35, 34, 33, 0.22); /* HACK: as above */
    padding-left: 0;
    padding-right: 0;
  }

  .fig:last-child {
    border-bottom: none;
  }
}
